/**
 * /ndt-inspection/invoices/:id — one NDI- invoice on its own page (batch 5,
 * fieldiq-api#1166). Opened from the NDT invoice register, Finance → Invoices,
 * an invoiced ticket's "On invoice" link, or a fresh Create invoice. The back
 * arrow returns to the register with its filters.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION. Lines, subtotal, each
 * levied tax (label, percent, amount), the tax's provenance and the total come
 * from `GET /ndt-inspection/invoices/:id` — the loader the PDF and the send
 * also use, so the page cannot disagree with what the customer receives.
 * Nothing here adds lines up or computes tax. A $0 line reads "No charge".
 *
 * ACTIONS (band, top right), for admin and office staff; a dispatcher reads:
 *   draft              Download PDF · Send · Void
 *   sent / approved    Download PDF · Send again · Mark paid · Void
 *   paid / void        Download PDF. Final.
 * Mark paid is the shared PATCH /unified-invoices/:id `{ status: 'paid' }`
 * (NDT has no paid door of its own; the unified route marks it paid as for
 * Pressure Testing). Void takes a reason; until fieldiq-api#1168 is live the
 * door answers 409 `void_not_available`, shown as a sentence, never the code.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Ban, CheckCircle2, Download, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useMarkNdtInvoicePaid, useNdtInvoice, useNdtInvoiceRecipient, useSendNdtInvoice, useVoidNdtInvoice } from '../../hooks/useNdtInvoices';
import { saveBlobAs } from '../../lib/blob-tab';
import { formatInstant } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ndtLocationRefLabel } from '../../lib/ndt-inspection';
import {
  canWriteNdtInvoices,
  ndtInvoiceCanMarkPaid,
  ndtInvoiceIsLive,
  ndtInvoiceLineGroups,
  ndtInvoicePdfFilename,
  ndtInvoiceRefusal,
  ndtInvoiceSendBlocked,
  ndtInvoiceStatusSkin,
  ndtLeviedTaxRows,
  ndtTaxProvenanceText,
  ndtTaxRowLabel,
} from '../../lib/ndt-invoices';
import { ndtQtyText, ndtRateText, ndtTicketScopeText } from '../../lib/ndt-tickets';
import type { NdtInvoice } from '../../types/ndt-invoices';
import { CcField, RecipientPreview, parseCcInput } from '../downhole/sendBits';
import { useBackToList } from '../downhole/recordPage';
import { NDT_INPUT, NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtDialog, NdtField, NdtFormError, NdtLoading, NdtPanel, NdtSpec, isNotFound, ndtDay } from './NdtBits';

const LIST = '/ndt-inspection/invoices';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60';

function SendDialog({ inv, onClose }: { inv: NdtInvoice; onClose: () => void }) {
  const tz = useTenantTimeZone();
  const recipient = useNdtInvoiceRecipient(inv.id);
  const send = useSendNdtInvoice();
  const resolved = recipient.data?.data?.to?.email ?? '';
  const [to, setTo] = useState<string | null>(null);
  const [ccText, setCcText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const toValue = to ?? resolved;
  const cc = parseCcInput(ccText);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const typed = toValue.trim();
    if (!typed) return setError('Enter an address to send this invoice to.');
    if (!EMAIL_RE.test(typed)) return setError(`"${typed}" is not a valid email address.`);
    if (cc.problem) return setError(cc.problem);
    // The resolver's own address is sent as no `to`, so the API records where it came from.
    const body = { id: inv.id, cc: cc.cc, ...(typed.toLowerCase() === resolved.toLowerCase() ? {} : { to: typed }) };
    send.mutate(body, {
      onSuccess: (res) => {
        toast.success(`${inv.invoiceNumber} sent to ${res?.sentTo ?? typed}`);
        onClose();
      },
      onError: (err) => setError(ndtInvoiceRefusal(err, 'The invoice was not sent.').message),
    });
  };

  return (
    <NdtDialog
      open
      title={`Send ${inv.invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={NDT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="ndt-send-invoice" disabled={send.isPending} data-testid="ndt-invoice-send-confirm" className={NDT_PRIMARY}>
            {send.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Send className="size-4" aria-hidden="true" />}
            Send with PDF
          </button>
        </>
      }
    >
      <form id="ndt-send-invoice" onSubmit={submit} className="space-y-4" noValidate>
        <RecipientPreview query={recipient} tz={tz} subject="who this invoice goes to" />
        <NdtField label="To" hint="The address above, unless you type another for this send.">
          <input className={NDT_INPUT} type="email" inputMode="email" value={toValue} onChange={(e) => setTo(e.target.value)} maxLength={255} data-testid="ndt-invoice-send-to" />
        </NdtField>
        <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
        <NdtFormError message={error} />
      </form>
    </NdtDialog>
  );
}

function VoidDialog({ inv, onClose }: { inv: NdtInvoice; onClose: () => void }) {
  const voidInvoice = useVoidNdtInvoice();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const count = inv.tickets.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('A reason is required to void an invoice.');
    voidInvoice.mutate(
      { id: inv.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${inv.invoiceNumber} voided`);
          onClose();
        },
        onError: (err) => setError(ndtInvoiceRefusal(err, 'The invoice was not voided.').message),
      },
    );
  };

  return (
    <NdtDialog
      open
      title={`Void ${inv.invoiceNumber}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={NDT_SECONDARY} onClick={onClose}>
            Keep invoice
          </button>
          <button
            type="submit"
            form="ndt-void-invoice"
            disabled={voidInvoice.isPending}
            data-testid="ndt-invoice-void-confirm"
            className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          >
            {voidInvoice.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Void invoice
          </button>
        </>
      }
    >
      <form id="ndt-void-invoice" onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-fieldiq-text">
          A void invoice is kept for the record and cannot be sent, paid or reopened. Its {count === 1 ? 'field ticket goes' : `${count} field tickets go`} back to
          approved, ready to invoice again.
        </p>
        <NdtFormError message={error} />
        <NdtField label="Reason">
          <textarea className={`${NDT_INPUT} h-24 py-2`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} data-testid="ndt-invoice-void-reason" autoFocus />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

function MarkPaidDialog({ inv, onClose }: { inv: NdtInvoice; onClose: () => void }) {
  const markPaid = useMarkNdtInvoicePaid();
  const [error, setError] = useState<string | null>(null);
  const confirm = () => {
    setError(null);
    markPaid.mutate(inv.id, {
      onSuccess: () => {
        toast.success(`${inv.invoiceNumber} marked paid`);
        onClose();
      },
      onError: (err) => setError(ndtInvoiceRefusal(err, 'The invoice was not marked paid.').message),
    });
  };
  return (
    <NdtDialog
      open
      title={`Mark ${inv.invoiceNumber} paid?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={NDT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={markPaid.isPending} data-testid="ndt-invoice-paid-confirm" className={NDT_PRIMARY}>
            {markPaid.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
            Mark paid
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fieldiq-text">
          Record that {inv.customerName || 'the customer'} paid {fmtCurrency(inv.total, inv.currency)}. A paid invoice is final: it cannot be sent or voided.
        </p>
        <NdtFormError message={error} />
      </div>
    </NdtDialog>
  );
}

export default function NdtInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user, company } = useAuth();
  // Tax fields follow the tenant's country (Jason, 2026-10-24): PST is a
  // Canadian tax, so a US tenant (or one whose country is not known) sees no
  // PST field. The levied tax rows below are the stored labels, as before.
  const showsPst = company?.country === 'CA';
  const tz = useTenantTimeZone();
  const query = useNdtInvoice(id);
  const inv = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [dialog, setDialog] = useState<'send' | 'void' | 'paid' | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canWrite = canWriteNdtInvoices(user?.role);
  const status = inv?.status ?? null;
  const live = ndtInvoiceIsLive(status);
  const sendBlocked = !!inv && ndtInvoiceSendBlocked(inv);

  const downloadPdf = async () => {
    if (!inv) return;
    setActionError(null);
    setPdfBusy(true);
    try {
      const res = await api.ndtInspection.invoices.pdf(inv.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      saveBlobAs(blob, ndtInvoicePdfFilename(inv));
    } catch (err) {
      setActionError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const cur = inv?.currency ?? null;
  const taxRows = inv ? ndtLeviedTaxRows(inv) : [];
  const ticketOf = (ticketId: string) => inv?.tickets.find((t) => t.id === ticketId);

  const actions = inv ? (
    <div className="flex flex-wrap gap-2" data-testid="ndt-invoice-actions">
      <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy} data-testid="ndt-invoice-pdf">
        {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
        PDF
      </button>
      {canWrite && live && (
        <button type="button" className={BAND_SECONDARY} onClick={() => setDialog('void')} data-testid="ndt-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && ndtInvoiceCanMarkPaid(status) && (
        <button type="button" className={BAND_SECONDARY} onClick={() => setDialog('paid')} data-testid="ndt-invoice-mark-paid">
          <CheckCircle2 className="size-4" aria-hidden="true" />
          Mark paid
        </button>
      )}
      {canWrite && live && (
        <button type="button" className={NDT_PRIMARY} onClick={() => setDialog('send')} disabled={sendBlocked} data-testid="ndt-invoice-send">
          <Send className="size-4" aria-hidden="true" />
          {inv.sentAt ? 'Send again' : 'Send'}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="ndt-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT invoice"
        title={inv ? inv.invoiceNumber || 'Invoice' : query.isLoading ? 'Loading…' : 'Invoice'}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="ndt-invoice-detail">
          {!inv && query.isLoading ? (
            <NdtLoading label="Loading the invoice" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ndt-invoice-not-found">
              This invoice was not found.
            </p>
          ) : !inv ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this invoice" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid="ndt-invoice-status">
                  <NdtBadge skin={ndtInvoiceStatusSkin(status)} value={status} />
                </span>
                {inv.jobId ? (
                  <Link to={`/ndt-inspection/jobs/${inv.jobId}`} className="text-sm font-bold text-fieldiq-text underline">
                    {inv.jobNumber || '—'}
                  </Link>
                ) : (
                  <span className="text-sm text-fieldiq-muted">Several jobs</span>
                )}
                <span className="text-sm text-fieldiq-muted">{inv.customerName || '—'}</span>
              </div>
              {failure && (
                <p role="status" className="text-xs text-fieldiq-muted">
                  This invoice could not be refreshed just now, so it shows what loaded earlier.
                </p>
              )}
              {status === 'void' && (
                <div role="status" data-testid="ndt-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {formatInstant(inv.voidedAt, tz) ?? ''}. This invoice is final and read-only.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.voidReason || '—'}</p>
                </div>
              )}
              {status === 'paid' && (
                <p role="status" data-testid="ndt-invoice-paid-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Paid {formatInstant(inv.paidAt, tz) ?? '—'}.
                </p>
              )}
              {sendBlocked && status !== 'void' && (
                <div role="note" data-testid="ndt-invoice-send-blocked" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">
                    Tax for {inv.jurisdiction || 'this province or state'}{' '}
                    {inv.taxFallbackReason === 'not_yet_effective' ? 'is not in effect yet on the invoice date' : 'is not set up'}, so the company&rsquo;s default rate
                    was used and this invoice cannot be sent.
                  </p>
                  <p className="mt-1">Add the rate in Settings, Regional &amp; Tax, then void this invoice and create it again.</p>
                </div>
              )}
              {actionError && (
                <p role="alert" data-testid="ndt-invoice-action-error" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  {actionError}
                </p>
              )}

              <NdtPanel eyebrow="Invoice" title="Details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
                  <NdtSpec label="Invoice date" value={ndtDay(inv.invoiceDate)} />
                  <NdtSpec label="Due date" value={ndtDay(inv.dueDate)} />
                  <NdtSpec label="Currency" value={inv.currency || '—'} />
                  <NdtSpec label="Customer" value={inv.customerName || '—'} />
                  <NdtSpec label="Billing address" value={inv.customerAddress || '—'} />
                  <NdtSpec label="Province / state" value={inv.jurisdiction || '—'} />
                  <NdtSpec label="Location" value={inv.locationName || '—'} />
                  <NdtSpec label={ndtLocationRefLabel(inv.jurisdiction, inv.currency)} value={inv.lsd || '—'} mono />
                  <NdtSpec label="Line / project" value={[inv.lineRef, inv.projectRef].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="PO" value={inv.poNumber || '—'} mono />
                  <NdtSpec label="AFE" value={inv.afeNumber || '—'} mono />
                  <NdtSpec label="Cost code" value={inv.costCode || '—'} mono />
                  <NdtSpec label="Last sent" value={inv.sentAt ? `${formatInstant(inv.sentAt, tz) ?? '—'} to ${inv.sentTo || '—'}` : 'Not sent'} />
                  {showsPst && <NdtSpec label="PST exemption" value={inv.pstExempt ? inv.pstExemptNumber || 'Exempt' : '—'} />}
                </dl>
                {inv.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{inv.notes}</p>}
              </NdtPanel>

              <NdtPanel eyebrow="Billed" title="Field tickets" testId="ndt-invoice-tickets">
                {inv.tickets.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-fieldiq-text">This invoice lists no field tickets.</p>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.tickets.map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="ndt-invoice-ticket">
                        <Link to={`/ndt-inspection/tickets/${t.id}`} className="font-mono font-extrabold text-white underline-offset-2 hover:underline">
                          {t.ticketNumber}
                        </Link>
                        <span className="text-fieldiq-text">{ndtTicketScopeText(t.scope, t.workDate)}</span>
                        <span className="text-fieldiq-muted">Signed by {t.signerName || '—'}</span>
                        <span className="font-mono text-fieldiq-muted">PO {t.poNumber || '—'}</span>
                        {t.releasedAt && <span className="text-fieldiq-muted">Released {formatInstant(t.releasedAt, tz) ?? '—'}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </NdtPanel>

              <NdtPanel eyebrow="Charges" title="Lines" testId="ndt-invoice-lines">
                {inv.lines.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-fieldiq-text">This invoice has no lines.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {ndtInvoiceLineGroups(inv).map((g) => {
                        const t = ticketOf(g.ticketId);
                        return (
                          <tbody key={g.ticketId} data-testid="ndt-invoice-line-group">
                            <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                              <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                                <span className="font-mono">{g.ticketNumber}</span>
                                {t?.workDate ? ` · ${ndtDay(t.workDate)}` : ''}
                                {` · Signed by ${t?.signerName || '—'}`}
                              </th>
                            </tr>
                            {g.lines.map((l) => (
                              <tr key={l.id} data-testid="ndt-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-white">{l.description || '—'}</p>
                                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                                    {[l.rateCode, l.method, l.workDate ? ndtDay(l.workDate) : null].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ndtQtyText(l.quantity, l.unit, l.distanceUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ndtRateText(l.rate, cur, l.unit, l.distanceUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white">{l.amount === 0 ? 'No charge' : fmtCurrency(l.amount, cur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Subtotal
                          </th>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white" data-testid="ndt-invoice-subtotal">
                            {fmtCurrency(inv.subtotal, cur)}
                          </td>
                        </tr>
                        {taxRows.length > 0 ? (
                          taxRows.map((r, i) => (
                            <tr key={i} className="border-l-4 border-l-transparent" data-testid="ndt-invoice-tax-row">
                              <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">
                                {ndtTaxRowLabel(r.label, r.percent)}
                              </th>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(r.amount, cur)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-l-4 border-l-transparent" data-testid="ndt-invoice-tax-row">
                            <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">
                              Tax
                            </th>
                            <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(inv.taxAmount, cur)}</td>
                          </tr>
                        )}
                        <tr className="border-l-4 border-l-transparent">
                          <td colSpan={4} className="px-4 pb-2 text-right text-xs text-fieldiq-muted" data-testid="ndt-invoice-tax-source">
                            Tax: {ndtTaxProvenanceText(inv)}
                          </td>
                        </tr>
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Total ({inv.currency || '—'})
                          </th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="ndt-invoice-total">
                            {fmtCurrency(inv.total, cur)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </NdtPanel>
            </>
          )}
        </div>
      </section>

      {inv && dialog === 'send' && <SendDialog inv={inv} onClose={() => setDialog(null)} />}
      {inv && dialog === 'void' && <VoidDialog inv={inv} onClose={() => setDialog(null)} />}
      {inv && dialog === 'paid' && <MarkPaidDialog inv={inv} onClose={() => setDialog(null)} />}
    </div>
  );
}
